import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { HttpStatusCode } from 'axios';

import { ConfirmationWarningDialog } from '@/components/dialogs/ConfirmationWarningDialog';
import { getErrorInfo, useGlobalErrorStore } from '@/components/dialogs/GlobalErrorDialog';
import { PasswordForm } from '@/pages/auth/components/PasswordForm';
import { FIRST_STEP } from '@/pages/auth/sign-up/constants';
import { useClearSignUpData } from '@/pages/auth/sign-up/hooks/useClearSignUpData';
import { useInviteNavigation } from '@/pages/auth/sign-up/hooks/useInviteNavigation';
import { useSignUpStoreState } from '@/pages/auth/sign-up/store/signUp.store';
import { useAuthMethods } from '@/shared/lib/auth';
import { AuthPaths } from '@/shared/lib/react-router/paths/auth.paths';
import { useOTPStoreSignUpVerifyData } from '@/shared/stores/otp.store';

import { warningMessage, type WarningType } from '../../data/warningMessage';

export const InviteCreatePasswordForm = () => {
  const { t } = useTranslation('auth-sign-up');
  const { goToPreviousStep, currentStepIndex, totalSteps, goToStep } = useInviteNavigation();
  const navigate = useNavigate();
  const verifyData = useOTPStoreSignUpVerifyData();
  const { userData } = useSignUpStoreState();
  const clearSignUpData = useClearSignUpData();
  const { signUp } = useAuthMethods();

  const { setGlobalError } = useGlobalErrorStore();
  const [isConfirmationEditDialogOpen, setIsConfirmationEditDialogOpen] = useState(false);
  const [typeOfWarning, setTypeOfWarning] = useState<WarningType>('default');

  const handleCloseDialog = () => {
    setIsConfirmationEditDialogOpen(false);
  };

  const handleProceedDialog = () => {
    clearSignUpData();
    goToStep(FIRST_STEP);

    setIsConfirmationEditDialogOpen(false);
  };

  const onSubmit = async (password: string) => {
    try {
      const { access_token, refresh_token } = await signUp({
        first_name: userData.firstName,
        last_name: userData.lastName,
        password,
        signup_token: verifyData.signup_token,
        invite_token: userData.inviteToken,
      });

      navigate(AuthPaths.SIGN_UP.FINAL, {
        state: { access_token, refresh_token },
      });
    } catch (error) {
      const { responseStatus } = getErrorInfo(error);

      // Error: bad signup token - go to the first step
      if (responseStatus === HttpStatusCode.BadRequest) {
        setTypeOfWarning('signupToken');
        setIsConfirmationEditDialogOpen(true);
      } else {
        setGlobalError(error);
      }
    }
  };

  return (
    <Fragment>
      <PasswordForm
        onSubmit={onSubmit}
        navigation={{
          totalSteps,
          goToPreviousStep,
          currentStep: currentStepIndex + 1,
        }}
      />
      <ConfirmationWarningDialog
        open={isConfirmationEditDialogOpen}
        onClose={handleCloseDialog}
        onProceed={handleProceedDialog}
        titleText={warningMessage[typeOfWarning].headerText}
        contentText={warningMessage[typeOfWarning].bodyText}
        actionButtonText={t('forms.pharmacy-form.dialogs.confirmation-warning-dialog.action-button-text')}
      />
    </Fragment>
  );
};
